// PC-819: the business editor spreads a single antd Form across nested tabs, so
// a rejected validateFields can leave the user staring at an untouched pane with
// no idea why Update did nothing. These helpers turn the rejection into a
// destination (which tabs to open) and a sentence (what to say).

export type EditorTabTarget = { outer: string; inner?: string };

type ErrorField = { name: (string | number)[]; errors: string[] };

// Root form field -> the inner Business Details pane that renders it. Keys are
// the first path segment only; nested paths (e.g. ["onlinePricing", "amex",
// "value"]) resolve through their root.
const INNER_TAB_BY_FIELD: Record<string, string> = {
  businessName: "companyProfile",
  businessRegistrationNumber: "companyProfile",
  businessTradeName: "companyProfile",
  businessType: "companyProfile",
  supportEmail: "companyProfile",
  supportPhone: "companyProfile",
  tinNumber: "companyProfile",
  vat: "companyProfile",
  vatNumber: "companyProfile",
  websiteURL: "companyProfile",
  address: "companyAddress",
  description: "mccIndustry",
  industry: "mccIndustry",
  mccComments: "mccIndustry",
  merchantCategoryCode: "mccIndustry",
  bankAccountIban: "bankAccount",
  bankAccountNumber: "bankAccount",
  bankAddress: "bankAccount",
  bankName: "bankAccount",
  bic: "bankAccount",
  logo: "persons",
  persons: "persons",
  onboardingFlags: "flags",
  merchantServiceChargeFees: "merchantServiceFees",
  onlinePricing: "merchantServiceFees",
  payoutOptions: "payoutDetails",
  settings: "settings",
  partnerId: "partnerMapper",
  partnerPurchPriceProfileId: "partnerMapper",
  softwareVendorPricing: "partnerISV",
  softwareVendorPricingTemplate: "partnerISV",
};

// Fields that live on an outer tab of their own rather than inside Business
// Details.
const OUTER_TAB_BY_FIELD: Record<string, string> = {
  generalComments: "generalComments",
  complianceContact: "complianceContact",
};

/** Which tab(s) to open so the offending field is on screen, or `undefined`
 *  when the field isn't one we can place (then we only report the failure). */
export function resolveEditorTabTarget(
  name: (string | number)[] | undefined
): EditorTabTarget | undefined {
  const root = name?.[0];
  if (typeof root !== "string") return undefined;
  if (OUTER_TAB_BY_FIELD[root]) return { outer: OUTER_TAB_BY_FIELD[root] };
  const inner = INNER_TAB_BY_FIELD[root];
  return inner ? { outer: "businessDetails", inner } : undefined;
}

/** One sentence saying nothing was saved, quoting the single reason when
 *  there is only one so the user doesn't have to hunt for it. */
export function describeBlockedSubmit(errorFields: ErrorField[]): string {
  const messages = errorFields.flatMap((f) => f.errors).filter(Boolean);
  if (messages.length === 1) return `Not saved: ${messages[0]}`;
  if (messages.length > 1) {
    return `Not saved — ${messages.length} fields need fixing. The first one is highlighted below.`;
  }
  return "Not saved — some fields are invalid.";
}
